<template>
  <section class="news-page h-full w-full flex flex-col">
    <!-- Hero Section -->
    <div :style="backgroundStyle" class="news-hero relative w-full overflow-hidden">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div class="max-w-3xl">
            <div class="hero-badge">
              <span>Latest Updates</span>
            </div>
            <h1 class="hero-title">News & Announcements</h1>
            <p class="hero-subtitle">Stay informed with the latest news, updates, and announcements from our community
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="news-content bg-gradient-to-b from-gray-50 to-white pt-4 pb-12 sm:pt-6 sm:pb-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Breadcrumb -->
        <div class="mb-4">
          <bread-crumb :items="breadCrumbItems"></bread-crumb>
        </div>

        <!-- Section Header -->
        <div class="section-header mb-8 sm:mb-10">
          <div class="flex items-center gap-4">
            <button @click.stop="$router.go(-1)" class="back-button-icon">
              <icon-back class="icon"></icon-back>
            </button>
            <div>
              <h2 class="section-title">Latest News</h2>
              <p v-if="news.paginatorInfo && news.paginatorInfo.total" class="section-count">
                {{ formatNumber(news.paginatorInfo.total) }} {{ news.paginatorInfo.total === 1 ? 'article' : 'articles'
                }} found
              </p>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="$apollo.queries.news.loading && page === 1" class="news-grid">
          <news-card-shimmer v-for="(story, index) in first" :key="index"></news-card-shimmer>
        </div>

        <!-- News Grid -->
        <div v-else-if="getNews && getNews.length > 0" class="news-grid">
          <news-card v-for="(news, index) in getNews" :key="index" :item="news"></news-card>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z">
              </path>
            </svg>
          </div>
          <h3 class="empty-title">No News Available</h3>
          <p class="empty-text">There are no news articles available at the moment. Please check back later.</p>
        </div>

        <!-- Load More Button -->
        <div v-if="showViewMoreButton" class="load-more-wrapper">
          <button @click="loadMore" class="load-more-btn group" :disabled="$apollo.queries.news.loading">
            <span v-if="!$apollo.queries.news.loading">Load More News</span>
            <span v-else class="loading-text">Loading...</span>
            <svg v-if="!$apollo.queries.news.loading" class="load-more-icon" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
            <svg v-else class="load-more-icon animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import gql from 'graphql-tag';
import NewsCard from "../../components/news/NewsCard.vue";
import NewsCardShimmer from "../../components/news/NewsCardShimmer.vue";
import IconBack from "../../components/icons/IconBack.vue";
import backgroundImage from '../../assets/images/news/img1.png';
import BreadCrumb from "../../components/shared/BreadCrumb.vue";

const itemPerPage = 12;

export default {
  name: "news",
  components: {
    NewsCard,
    NewsCardShimmer,
    IconBack,
    BreadCrumb,
  },
  data() {
    return {
      news: [],
      first: itemPerPage,
      page: 1,
      breadCrumbItems: [
        { label: 'Home', path: '/' },
        { label: 'Latest News' },
      ],
    };
  },
  computed: {
    backgroundStyle() {
      return {
        'background-image': `url(${backgroundImage})`
      }
    },
    getNews() {
      // With update() function, this.news is now the publicNews result directly
      if (!this.news) {
        return [];
      }
      // Check if there's an error
      if (this.news.error) {
        return [];
      }
      return this.news.data || [];
    },
    showViewMoreButton() {
      if (!this.news) return false;
      if (this.news.error) return false;
      if (!this.news.paginatorInfo) return false;
      return this.news.paginatorInfo.hasMorePages || false;
    },
  },
  apollo: {
    news: {
      query: gql`query GetLatestNews($first: Int!, $page: Int!) {
                    publicNews(first: $first, page: $page) {
                        error
                        error_code
                        message
                        data {
                            description
                            id
                            slug
                            thumbnail
                            title
                            news_date
                            entity
                        }
                        paginatorInfo {
                            currentPage
                            lastPage
                            perPage
                            total
                            hasMorePages
                            firstItem
                            lastItem
                        }
                    }
                }`,
      variables() {
        return {
          first: itemPerPage,
          page: 1,
        }
      },
      update(data) {
        // Transform the result: return publicNews directly so this.news = publicNews result
        return data.publicNews;
      },
      // fetchPolicy: 'cache-and-network'
    },
  },
  methods: {
    formatNumber(num) {
      if (!num) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    loadMore() {
      this.page += 1;
      this.$apollo.queries.news.fetchMore({
        variables: {
          first: this.first,
          page: this.page,
        },

        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newNews = fetchMoreResult.publicNews.data;
          const newPaginatorInfo = fetchMoreResult.publicNews.paginatorInfo;

          // Return the publicNews structure directly since we're using update()
          return {
            ...previousResult,
            data: [...previousResult.data, ...newNews],
            paginatorInfo: newPaginatorInfo,
          }
        }
      })
    },
  },

}
</script>
<style lang="scss" scoped>
.news-hero {
  height: 400px;
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  @media (min-width: 640px) {
    height: 450px;
  }

  @media (min-width: 1024px) {
    height: 500px;
  }

  @media (min-width: 1280px) {
    height: 550px;
  }
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(57, 72, 103, 0.85) 0%, rgba(73, 157, 212, 0.75) 100%);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  height: 100%;
  color: white;
}

.hero-badge {
  display: inline-block;
  margin-bottom: 1rem;
}

.hero-badge span {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 1024px) {
    font-size: 3.5rem;
  }
}

.hero-subtitle {
  font-size: 1.125rem;
  line-height: 1.7;
  opacity: 0.95;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  max-width: 2xl;

  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
}

.section-header {
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #e2e8f0;
}

.back-button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(57, 72, 103, 0.3);
  }

  .icon {
    width: 1.25rem;
    height: 1.25rem;
  }
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
  line-height: 1.3;

  @media (min-width: 640px) {
    font-size: 1.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
}

.section-count {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;

  @media (min-width: 640px) {
    font-size: 1rem;
  }
}

.news-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 5rem;
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  border-radius: 50%;
  margin-bottom: 1.5rem;
  color: #9ca3af;

  svg {
    width: 2.5rem;
    height: 2.5rem;
  }
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;

  @media (min-width: 640px) {
    font-size: 1.875rem;
  }
}

.empty-text {
  font-size: 1rem;
  color: #64748b;
  max-width: 32rem;
  margin: 0 auto;

  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
}

.load-more-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.load-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px -2px rgba(0, 0, 0, 0.15);
    background: linear-gradient(135deg, #499DD4 0%, #394867 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .load-more-icon {
    width: 1.25rem;
    height: 1.25rem;
    transition: transform 0.3s ease;
  }

  &:hover:not(:disabled) .load-more-icon {
    transform: translateY(4px);
  }
}

.loading-text {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
</style>